import Select from "react-select";
import { selectStyles } from "../Constants";

const TableConfigSection = ({
  tableConfig,
  setTableConfig,
  invoiceType,
  formData,
  setFormData,
}) => {
  const tableTypeOptions = [
    { value: "item-price", label: "Item & Price" },
    { value: "item-quantity", label: "Item & Quantity" },
    {
      value: "document-description-copies",
      label: "Document, Description & Copies",
    },
  ];

  const predefinedTableTypes = {
    "item-price": [
      { key: "item", header: "Item", type: "text" },
      { key: "price", header: "Price", type: "currency" },
    ],
    "item-quantity": [
      { key: "item", header: "Item", type: "text" },
      { key: "quantity", header: "No of Items", type: "number" },
    ],
    "document-description-copies": [
      { key: "document", header: "Document", type: "text" },
      { key: "description", header: "Description", type: "textarea" },
      { key: "copies", header: "No. of Copies", type: "number" },
    ],
  };

  // Fixed helper function to create empty item based on table structure
  const createEmptyItem = (columns) => {
    const newItem = {};
    columns.forEach((col) => {
      if (col.type === "number") {
        newItem[col.key] = 1;
      } else if (col.type === "currency") {
        newItem[col.key] = 0;
      } else {
        newItem[col.key] = "";
      }
    });
    return newItem;
  };

  // Helper function to get current items array (handles both array and object structures)
  const getCurrentItems = () => {
    if (!formData.items) return [];

    if (Array.isArray(formData.items)) {
      return formData.items;
    }

    // Handle object structure
    if (formData.items.list && Array.isArray(formData.items.list)) {
      return formData.items.list;
    }

    if (formData.items.documents && Array.isArray(formData.items.documents)) {
      return formData.items.documents;
    }

    return [];
  };

  // Helper function to set items array (preserves original structure)
  const setCurrentItems = (newItems) => {
    setFormData((prev) => {
      // If items was originally an array, keep it as an array
      if (Array.isArray(prev.items)) {
        return { ...prev, items: newItems };
      }

      // If items was an object, preserve the structure
      if (prev.items && typeof prev.items === "object") {
        if (prev.items.list !== undefined) {
          return { ...prev, items: { ...prev.items, list: newItems } };
        }
        if (prev.items.documents !== undefined) {
          return { ...prev, items: { ...prev.items, documents: newItems } };
        }
      }

      // Default: set as array
      return { ...prev, items: newItems };
    });
  };

  // Helper function to migrate existing items to new structure
  const migrateItems = (oldItems, oldColumns, newColumns) => {
    if (!oldItems || oldItems.length === 0) {
      return [createEmptyItem(newColumns)];
    }

    return oldItems.map((item) => {
      const newItem = createEmptyItem(newColumns);

      // Try to preserve data where column keys match
      newColumns.forEach((newCol) => {
        if (item.hasOwnProperty(newCol.key)) {
          newItem[newCol.key] = item[newCol.key];
        }
      });

      return newItem;
    });
  };

  const handleTableTypeChange = (newType) => {
    const newColumns = predefinedTableTypes[newType];
    const oldColumns = tableConfig.customColumns;
    const currentItems = getCurrentItems();

    // Migrate existing items to new structure
    const migratedItems = migrateItems(currentItems, oldColumns, newColumns);

    // Update both table config and form data
    setTableConfig((prev) => ({
      ...prev,
      tableType: newType,
      customColumns: newColumns,
    }));

    setFormData((prev) => ({
      ...prev,
      tableConfig: {
        ...prev.tableConfig,
        tableType: newType,
        customColumns: newColumns,
      },
    }));

    // Set the migrated items
    setCurrentItems(migratedItems);
  };

  const handleTableInclusionChange = (includeTable) => {
    // Prevent unnecessary updates if the value hasn't changed
    if (formData.tableIncluded === includeTable) {
      return;
    }

    // Update table config
    setTableConfig((prev) => ({
      ...prev,
      includeTable,
    }));

    // Update form data with proper synchronization
    setFormData((prev) => {
      // Double check to prevent race conditions
      if (prev.tableIncluded === includeTable) {
        return prev; // Return unchanged state
      }

      const newFormData = {
        ...prev,
        tableIncluded: includeTable, // This is the key property
        tableConfig: {
          ...prev.tableConfig,
          includeTable,
        },
      };

      // Handle items based on table inclusion
      if (includeTable) {
        // If including table and no items exist, create one empty item
        const currentItems = Array.isArray(prev.items)
          ? prev.items
          : prev.items?.list || prev.items?.documents || [];

        if (currentItems.length === 0) {
          // Use setCurrentItems logic but apply it directly to newFormData
          if (Array.isArray(prev.items)) {
            newFormData.items = [createEmptyItem(tableConfig.customColumns)];
          } else if (prev.items && typeof prev.items === "object") {
            if (prev.items.list !== undefined) {
              newFormData.items = {
                ...prev.items,
                list: [createEmptyItem(tableConfig.customColumns)],
              };
            } else if (prev.items.documents !== undefined) {
              newFormData.items = {
                ...prev.items,
                documents: [createEmptyItem(tableConfig.customColumns)],
              };
            } else {
              newFormData.items = [createEmptyItem(tableConfig.customColumns)];
            }
          } else {
            newFormData.items = [createEmptyItem(tableConfig.customColumns)];
          }
        }
        // If items already exist, don't modify them
      } else {
        // If not including table, clear items
        if (Array.isArray(prev.items)) {
          newFormData.items = [];
        } else if (prev.items && typeof prev.items === "object") {
          if (prev.items.list !== undefined) {
            newFormData.items = { ...prev.items, list: [] };
          } else if (prev.items.documents !== undefined) {
            newFormData.items = { ...prev.items, documents: [] };
          } else {
            newFormData.items = [];
          }
        } else {
          newFormData.items = [];
        }
      }

      return newFormData;
    });
  };

  // Use formData.tableIncluded as the primary source of truth
  const isTableIncluded = formData.tableIncluded === true;

  return (
    <div className="m-4 p-4 bg-white/5 rounded-lg border border-alt-color dark:border-gray-600">
      <h3 className="text-lg font-semibold mb-4">Table Configuration</h3>

      {/* Include Table Toggle - Fixed to use formData.tableIncluded */}
      <div className="mb-4">
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isTableIncluded}
            onChange={(e) => {
              handleTableInclusionChange(e.target.checked);
            }}
            className="w-4 h-4 cursor-pointer"
          />
          <span className="text-sm">Include table in document</span>
        </label>
      </div>

      {isTableIncluded && (
        <>
          {/* Table Type Selection */}
          <div className="mb-4">
            <label className="block text-sm">Table Format</label>
            <Select
              value={tableTypeOptions.find(
                (opt) => opt.value === tableConfig.tableType
              )}
              onChange={(selected) => handleTableTypeChange(selected.value)}
              options={tableTypeOptions}
              styles={selectStyles}
              classNamePrefix="react-select"
              isSearchable={false} // optional: turn off search
            />
          </div>
        </>
      )}
    </div>
  );
};

export default TableConfigSection;
